import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { checkAndAwardBadgesForEvent } from '@/lib/badges';
import { getUserProgress } from '@/lib/gamification';

const currencies = ['EUR', 'USD', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'CNY'];

interface ProfileEditDialogProps {
  initialName: string;
  initialBudget: number;
  initialCurrency: string;
  onSave: () => void;
  trigger?: React.ReactNode;
}

export function ProfileEditDialog({
  initialName,
  initialBudget,
  initialCurrency,
  onSave,
  trigger,
}: ProfileEditDialogProps) {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(initialName);
  const [budget, setBudget] = useState(initialBudget.toString());
  const [currency, setCurrency] = useState(initialCurrency);
  const [saving, setSaving] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setName(initialName);
      setBudget(initialBudget.toString());
      setCurrency(initialCurrency);
    }
  }, [open, initialName, initialBudget, initialCurrency]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      // Save name to localStorage
      if (name) {
        localStorage.setItem('userName', name);
      }

      // Save budget and currency to Supabase
      const { error } = await supabase.from('budget_settings').upsert({
        user_id: user.id,
        monthly_budget: parseFloat(budget) || initialBudget,
        currency: currency,
      });

      if (error) throw error;

      toast({
        description: t('toast.saved'),
        duration: 1500,
      });

      // Check for 'named' badge if name was set
      if (name && name.trim().length > 0) {
        const progress = await getUserProgress(user.id);
        await checkAndAwardBadgesForEvent(user.id, 'profile_saved', progress);
      }

      setOpen(false);
      onSave();
    } catch (error) {
      console.error('Save profile error:', error);
      toast({
        title: t('common.error'),
        description: t('toast.saveFailed'),
        variant: 'destructive',
        duration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="gap-2">
            <Pencil className="h-4 w-4" />
            {t('profile.editProfile')}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          <DialogHeader>
            <DialogTitle>{t('profile.editProfile')}</DialogTitle>
            <DialogDescription>
              {t('profile.editProfileDesc')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="edit-name">{t('settings.name')}</Label>
              <Input
                id="edit-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('settings.name')}
                className="text-lg"
              />
              <p className="text-xs text-muted-foreground">{t('settings.nameHelp')}</p>
            </div>

            {/* Budget + Currency */}
            <div className="flex gap-3">
              <div className="flex-1 space-y-2">
                <Label htmlFor="edit-budget">{t('settings.monthlyBudget')}</Label>
                <Input
                  id="edit-budget"
                  type="number"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  className="text-lg"
                />
              </div>
              <div className="w-24 space-y-2">
                <Label htmlFor="edit-currency">{t('settings.currency')}</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger id="edit-currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((cur) => (
                      <SelectItem key={cur} value={cur}>
                        {cur}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={saving}
            >
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}
